import { useEffect, useState } from "react";
import api from "../lib/api";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { MapPin, Calendar, Sparkles, Users, Zap, Shield, ArrowRight, Ticket } from "lucide-react";
import { useNavigate } from "react-router-dom";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Countdown from "../components/Countdown";
import AddToCalendar from "../components/AddToCalendar";
import Typewriter from "../components/Typewriter";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

export default function Landing() {
  const [config, setConfig] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/public/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center text-ozx-muted text-sm tracking-widest uppercase">
        Carregando experiência...
      </div>
    );
  }

  const { event, appearance, tickets, lots = [], current_lots = {}, speakers = [] } = config;
  const ticketWithLot = (t) => current_lots[t.ticket_type_id] || lots.find((l) => l.ticket_type_id === t.ticket_type_id && l.is_active);

  // Formata o intervalo de datas do evento no card "Data"
  // Ex: start_date="2026-10-08T09:00:00-03:00", end_date="2026-10-09T22:00:00-03:00"
  // Linha 1: "08 e 09 Outubro" (ou "08 Out e 02 Nov" se cair em meses diferentes)
  // Linha 2: "2026 · 09h às 22h"
  const formatEventDateLabel = () => {
    try {
      const start = new Date(event.start_date);
      const end = new Date(event.end_date);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return { line1: "Em breve", line2: "" };
      }
      const months = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
      const monthsShort = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
      const dd = (d) => String(d.getDate()).padStart(2, "0");
      const hh = (d) => String(d.getHours()).padStart(2, "0");
      const sameDay = start.toDateString() === end.toDateString();
      const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
      let line1;
      if (sameDay) {
        line1 = `${dd(start)} ${months[start.getMonth()]}`;
      } else if (sameMonth) {
        line1 = `${dd(start)} e ${dd(end)} ${months[start.getMonth()]}`;
      } else {
        line1 = `${dd(start)} ${monthsShort[start.getMonth()]} a ${dd(end)} ${monthsShort[end.getMonth()]}`;
      }
      const startH = hh(start);
      const endH = hh(end);
      const line2 = `${start.getFullYear()} · ${startH}h às ${endH}h`;
      return { line1, line2 };
    } catch (e) {
      return { line1: event.short_pitch || "Em breve", line2: "" };
    }
  };
  const dateLabel = formatEventDateLabel();

  return (
    <div className="min-h-screen bg-ozx-bg text-white relative overflow-x-hidden">
      <Navbar logoUrl={appearance.logo_url} logoSize={appearance.logo_size} />

      {/* HERO */}
      <section className="relative min-h-[88vh] lg:min-h-screen flex items-center lg:items-end pb-12 lg:pb-24 pt-24 lg:pt-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={appearance.hero_image_url} alt="" className="w-full h-full object-cover opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-b from-ozx-bg/60 via-ozx-bg/40 to-ozx-bg" />
          <div className="absolute inset-0 bg-gradient-to-r from-ozx-bg via-transparent to-transparent" />
          {/* Floating background orbs (loop) */}
          <motion.div
            className="absolute top-1/4 right-1/3 h-72 w-72 rounded-full bg-ozx-primary/10 blur-[100px]"
            animate={{ x: [0, 60, 0], y: [0, -40, 0] }}
            transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute bottom-1/4 left-1/4 h-80 w-80 rounded-full bg-ozx-secondary/30 blur-[120px]"
            animate={{ x: [0, -50, 0], y: [0, 40, 0] }}
            transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 lg:gap-12 items-center">

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 glass-card rounded-full px-4 py-1.5 mb-8" data-testid="hero-badge">
              <span className="h-1.5 w-1.5 rounded-full bg-ozx-primary animate-pulse" />
              <span className="text-xs tracking-[0.25em] uppercase text-ozx-muted">{event.short_pitch}</span>
            </div>

            {appearance.hero_secondary_logo_url && (
              <motion.img
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.15 }}
                src={appearance.hero_secondary_logo_url}
                alt=""
                className="h-16 sm:h-20 lg:h-24 w-auto mb-4"
                data-testid="hero-secondary-logo"
              />
            )}

            <h1 className="font-display text-4xl sm:text-6xl lg:text-[88px] font-semibold tracking-[-0.03em] leading-[0.95] mb-3" data-testid="hero-headline">
              {event.hero_headline}<span className="text-ozx-primary">.</span>
            </h1>

            <div className="relative inline-block mb-8" data-testid="hero-location">
              <motion.div
                className="absolute inset-0 bg-ozx-primary/30 blur-2xl rounded-full"
                animate={{ opacity: [0.5, 0.9, 0.5], scale: [1, 1.1, 1] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              />
              <Typewriter
                text={(event.location_city || "São Paulo").toUpperCase()}
                speed={130}
                eraseSpeed={90}
                startDelay={900}
                holdAfterTyped={10000}
                holdAfterErased={700}
                loop
                className="relative font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.02em] text-ozx-primary inline-flex items-baseline"
                cursorClassName="text-ozx-primary"
                testId="hero-location-typewriter"
              />
            </div>

            <div className="block mb-8 min-h-[4.5em]">
              <Typewriter
                text={event.hero_subheadline}
                speed={32}
                startDelay={2400}
                showCursor={false}
                className="block text-base sm:text-lg text-ozx-muted max-w-xl leading-relaxed"
                testId="hero-subheadline"
              />
            </div>

            <div className="mb-10">
              <p className="text-xs uppercase tracking-[0.25em] text-ozx-primary mb-4">A experiência começa em</p>
              <Countdown target={event.start_date} />
            </div>

            <div className="flex flex-wrap gap-4">
              <motion.div
                animate={{ scale: [1, 1.03, 1] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                className="inline-block"
              >
                <Button
                  onClick={() => navigate("/checkout")}
                  className="bg-ozx-primary hover:bg-ozx-primaryHover text-ozx-bg font-semibold rounded-full px-8 py-6 text-base glow-primary"
                  data-testid="hero-cta-primary"
                >
                  {event.cta_primary} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </motion.div>
              <AddToCalendar event={event} />
            </div>
          </motion.div>

          {appearance.hero_side_image_url && (
            <motion.div
              initial={{ opacity: 0, scale: 0.7, x: 120, rotate: 8 }}
              animate={{ opacity: 1, scale: 1, x: 0, rotate: 0 }}
              transition={{ duration: 1.4, delay: 0.4, type: "spring", stiffness: 60, damping: 14 }}
              className="relative hidden lg:block"
            >
              <motion.div
                className="absolute -inset-10 bg-ozx-primary/15 blur-[80px] rounded-full"
                animate={{ scale: [1, 1.15, 1], opacity: [0.6, 1, 0.6] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              />
              <img
                src={appearance.hero_side_image_url}
                alt=""
                className="relative w-[480px] xl:w-[560px] h-auto object-contain"
                style={{ filter: "drop-shadow(0 30px 60px rgba(40, 185, 252, 0.25))" }}
                data-testid="hero-side-image"
              />
            </motion.div>
          )}

          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-white/5 py-6 bg-ozx-bg2/40 overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap">
          {Array(2).fill(0).map((_, i) => (
            <div key={i} className="flex items-center gap-8 px-4 font-display text-2xl sm:text-3xl">
              {(appearance.marquee_words && appearance.marquee_words.length > 0
                ? appearance.marquee_words.concat(appearance.marquee_words)
                : []).map((t, j) => (
                <span key={j} className="flex items-center gap-8">
                  <span className="text-white/80">{t}</span>
                  <span className="text-ozx-primary">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* SOBRE / BENTO */}
      <section id="sobre" className="py-24 lg:py-32 max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div {...fadeUp} className="max-w-3xl mb-16">
          <p className="text-xs uppercase tracking-[0.25em] text-ozx-primary mb-4">Sobre o evento</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight mb-6">
            Dois dias. Uma experiência inesquecível.
          </h2>
          <p className="text-ozx-muted text-lg leading-relaxed">{event.description}</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 lg:gap-6">
          <motion.div {...fadeUp} className="md:col-span-7 glass-card rounded-3xl p-8 lg:p-10 relative overflow-hidden">
            <Sparkles className="w-8 h-8 text-ozx-primary mb-6" />
            <h3 className="font-display text-3xl font-medium mb-3" data-testid="about-card1-title">{appearance.about_card1_title || "Programação imersiva"}</h3>
            <p className="text-ozx-muted leading-relaxed" data-testid="about-card1-text">{appearance.about_card1_text || "Shows, palestras, painéis exclusivos, lounges premium e ativações de marca cuidadosamente curadas. Cada minuto pensado para você."}</p>
            <div className="absolute -bottom-8 -right-8 h-40 w-40 rounded-full bg-ozx-primary/10 blur-3xl" />
          </motion.div>

          <motion.div {...fadeUp} transition={{ duration: 0.8, delay: 0.1 }} className="md:col-span-5 glass-card rounded-3xl p-8 lg:p-10">
            <Calendar className="w-8 h-8 text-ozx-primary mb-6" />
            <h3 className="font-display text-2xl font-medium mb-3">Data</h3>
            <p className="text-2xl font-display text-white" data-testid="event-date-line1">{dateLabel.line1}</p>
            {dateLabel.line2 && <p className="text-ozx-muted text-sm mt-1" data-testid="event-date-line2">{dateLabel.line2}</p>}
          </motion.div>

          <motion.div {...fadeUp} transition={{ duration: 0.8, delay: 0.15 }} className="md:col-span-5 glass-card rounded-3xl p-8 lg:p-10">
            <MapPin className="w-8 h-8 text-ozx-primary mb-6" />
            <h3 className="font-display text-2xl font-medium mb-3">Local</h3>
            <p className="text-xl font-display text-white">{event.location_name}</p>
            <p className="text-ozx-muted text-sm mt-1">{event.location_address}</p>
          </motion.div>

          <motion.div {...fadeUp} transition={{ duration: 0.8, delay: 0.2 }} className="md:col-span-7 glass-card rounded-3xl p-8 lg:p-10 relative overflow-hidden">
            <Users className="w-8 h-8 text-ozx-primary mb-6" />
            <h3 className="font-display text-3xl font-medium mb-3" data-testid="about-card2-title">{appearance.about_card2_title || "Networking de alto nível"}</h3>
            <p className="text-ozx-muted leading-relaxed" data-testid="about-card2-text">{appearance.about_card2_text || "Conecte-se com fundadores, criadores e líderes de marca em ambientes pensados para conversas que importam."}</p>
            <div className="absolute -bottom-12 -left-12 h-48 w-48 rounded-full bg-ozx-secondary/30 blur-3xl" />
          </motion.div>
        </div>
      </section>

      {/* GALLERY */}
      <section id="galeria" className="py-24 lg:py-32 max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div {...fadeUp} className="mb-12">
          <p className="text-xs uppercase tracking-[0.25em] text-ozx-primary mb-3">Galeria</p>
          <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight">O que esperar</h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
          {appearance.gallery_images.map((src, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className={`overflow-hidden rounded-2xl glass-card ${i === 0 ? "md:col-span-2 md:row-span-2 md:aspect-square" : "aspect-[4/5]"}`}
            >
              <img src={src} alt={`gallery-${i}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </motion.div>
          ))}
        </div>
      </section>

      {/* SPEAKERS */}
      {speakers.length > 0 && (
        <section id="palestrantes" className="py-24 lg:py-32 max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp} className="mb-12 text-center max-w-2xl mx-auto">
            <p className="text-xs uppercase tracking-[0.25em] text-ozx-primary mb-3">Palestrantes</p>
            <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight">Quem sobe no palco</h2>
            <p className="text-ozx-muted mt-4">Nomes que estão moldando o futuro de marcas, criadores e experiências.</p>
          </motion.div>
          <div className="flex flex-wrap justify-center gap-6 lg:gap-8" data-testid="speakers-grid">
            {speakers.map((s, i) => (
              <motion.div
                key={s.speaker_id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className="glass-card rounded-3xl p-6 w-full sm:w-[300px] flex flex-col items-center text-center group hover:border-ozx-primary/40 transition-all"
                data-testid={`speaker-card-${s.speaker_id}`}
              >
                <div className="relative w-32 h-32 mb-5 rounded-full overflow-hidden ring-2 ring-ozx-primary/30 group-hover:ring-ozx-primary/70 transition">
                  {s.photo_url ? (
                    <img src={s.photo_url} alt={s.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-ozx-primary/30 to-ozx-secondary/40 flex items-center justify-center text-3xl font-display text-white">
                      {s.name?.charAt(0) || "?"}
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-ozx-bg/60 to-transparent opacity-0 group-hover:opacity-100 transition" />
                </div>
                <h3 className="font-display text-xl font-medium mb-2">{s.name}</h3>
                {s.description && (
                  <p className="text-sm text-ozx-muted leading-relaxed">{s.description}</p>
                )}
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* TICKETS */}
      <section id="ingressos" className="py-24 lg:py-32 max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div {...fadeUp} className="text-center mb-16 max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-[0.25em] text-ozx-primary mb-3">Ingressos</p>
          <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight">Escolha sua experiência</h2>
          <p className="text-ozx-muted mt-4">Inclua acompanhante no checkout e receba duas credenciais individualizadas.</p>
        </motion.div>

        <div className="flex flex-wrap justify-center gap-6 max-w-6xl mx-auto" data-testid="tickets-grid">
          {tickets.map((t, i) => {
            const lot = ticketWithLot(t);
            // collect all lots for this ticket, sorted by order
            const ticketLots = lots.filter((l) => l.ticket_type_id === t.ticket_type_id).sort((a, b) => (a.order || 0) - (b.order || 0));
            const visibleLot = lot || ticketLots[0];
            const isSoldOut = visibleLot ? !visibleLot.is_available : true;
            const isComingSoon = !!t.coming_soon;
            return (
            <motion.div
              key={t.ticket_type_id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className={`relative glass-card rounded-3xl p-8 lg:p-10 group transition-all w-full sm:w-[420px] lg:w-[380px] ${isSoldOut ? "opacity-60" : "hover:border-ozx-primary/40"}`}
              data-testid={`ticket-card-${t.ticket_type_id}`}
            >
              {visibleLot && (
                <div className={`absolute top-6 right-6 px-3 py-1 rounded-full text-xs tracking-wider uppercase ${isSoldOut ? "bg-white/10 border border-white/20 text-ozx-muted" : "bg-ozx-primary/15 border border-ozx-primary/30 text-ozx-primary"}`}>
                  {visibleLot.name}
                </div>
              )}
              <Ticket className={`w-8 h-8 mb-6 ${isSoldOut ? "text-ozx-muted" : "text-ozx-primary"}`} />
              <h3 className="font-display text-3xl font-medium mb-2">{t.name}</h3>
              <p className="text-ozx-muted text-sm leading-relaxed mb-6">{t.description}</p>
              {isComingSoon ? (
                <div className="mb-4" data-testid="ticket-coming-soon-label">
                  <p className="font-display text-3xl text-ozx-primary mb-1">Disponível em breve</p>
                  <p className="text-sm text-ozx-muted">Aguarde — novidades em instantes</p>
                </div>
              ) : visibleLot ? (
                <div className="mb-4">
                  {(() => {
                    const fmt = (v) => Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    const hasInstallment = visibleLot.installments_count > 0 && Number(visibleLot.installment_price) > 0;
                    const hasCash = Number(visibleLot.cash_price) > 0;
                    if (isSoldOut) {
                      return (
                        <div className="mb-2">
                          {hasInstallment ? (
                            <span className="font-display text-3xl font-semibold text-ozx-muted line-through">{visibleLot.installments_count}x R$ {fmt(visibleLot.installment_price)}</span>
                          ) : (
                            <span className="font-display text-4xl font-semibold text-ozx-muted line-through">R$ {fmt(visibleLot.price)}</span>
                          )}
                          <p className="font-display text-3xl text-ozx-danger mt-1">ESGOTADO</p>
                        </div>
                      );
                    }
                    if (hasInstallment || hasCash) {
                      return (
                        <>
                          {hasInstallment && (
                            <>
                              <span className="text-ozx-muted text-sm">a partir de</span>
                              <div className="flex items-baseline gap-2" data-testid="ticket-installment-price">
                                <span className="font-display text-5xl font-semibold leading-none">{visibleLot.installments_count}x</span>
                                <span className="font-display text-4xl font-semibold">R$ {fmt(visibleLot.installment_price)}</span>
                              </div>
                            </>
                          )}
                          {hasCash && (
                            <p className="text-ozx-muted text-sm mt-2" data-testid="ticket-cash-price">
                              ou <span className="text-white font-medium">R$ {fmt(visibleLot.cash_price)}</span> à vista
                            </p>
                          )}
                          {!hasInstallment && hasCash && (
                            <span className="text-ozx-muted text-xs">preço à vista</span>
                          )}
                        </>
                      );
                    }
                    return (
                      <>
                        <span className="text-ozx-muted text-sm">a partir de</span>
                        <div className="flex items-baseline gap-2">
                          <span className="font-display text-5xl font-semibold">R$ {fmt(visibleLot.price)}</span>
                        </div>
                      </>
                    );
                  })()}
                  {visibleLot.valid_until && !isSoldOut && (
                    <p className="text-xs text-ozx-muted mt-3">Válido até {new Date(visibleLot.valid_until).toLocaleDateString("pt-BR")}</p>
                  )}
                </div>
              ) : (
                <p className="text-ozx-muted text-sm mb-6">Em breve</p>
              )}
              <ul className="space-y-2 mb-8" data-testid={`ticket-benefits-${t.ticket_type_id}`}>
                {(Array.isArray(t.benefits) && t.benefits.length > 0
                  ? t.benefits
                  : ["Acesso aos 2 dias do evento", "Áreas premium e lounges", "Networking exclusivo", "Credencial digital com QR Code"]
                ).map((f, idx) => (
                  <li key={`${f}-${idx}`} className="flex items-center gap-2 text-sm text-ozx-muted">
                    <Zap className={`w-3.5 h-3.5 ${isSoldOut ? "text-ozx-muted" : "text-ozx-primary"}`} /> {f}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => navigate(`/checkout?ticket=${t.ticket_type_id}`)}
                disabled={isSoldOut || isComingSoon}
                className={`w-full font-semibold rounded-full py-6 ${(isSoldOut || isComingSoon) ? "bg-white/10 text-ozx-muted cursor-not-allowed" : "bg-ozx-primary hover:bg-ozx-primaryHover text-ozx-bg"}`}
                data-testid={`ticket-buy-${t.ticket_type_id}`}
              >
                {isComingSoon ? "Em breve" : isSoldOut ? "Esgotado" : <>Comprar Agora <ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>
            </motion.div>
            );
          })}
        </div>

        <motion.div {...fadeUp} className="mt-12 flex justify-center">
          <div className="glass-card rounded-2xl px-6 py-4 flex items-center gap-3 max-w-md text-center">
            <Shield className="w-5 h-5 text-ozx-primary flex-shrink-0" />
            <p className="text-sm text-ozx-muted">Pagamentos 100% seguros via <span className="text-white">PagBank</span>. Dados criptografados.</p>
          </div>
        </motion.div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 lg:py-32 max-w-3xl mx-auto px-6 lg:px-12">
        <motion.div {...fadeUp} className="mb-12 text-center">
          <p className="text-xs uppercase tracking-[0.25em] text-ozx-primary mb-3">Perguntas</p>
          <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight">Tirando suas dúvidas</h2>
        </motion.div>
        <Accordion type="single" collapsible className="space-y-3">
          {(appearance.faq || []).map((f, idx) => (
            <AccordionItem
              key={idx}
              value={`item-${idx}`}
              className="glass-card rounded-2xl px-6 border-0"
              data-testid={`faq-${idx}`}
            >
              <AccordionTrigger className="text-left font-display text-lg hover:no-underline py-5">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-ozx-muted pb-5 leading-relaxed">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CTA */}
      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div {...fadeUp} className="relative overflow-hidden glass-card rounded-3xl p-12 lg:p-20 text-center">
          <div className="absolute inset-0 bg-gradient-to-br from-ozx-secondary/40 via-transparent to-ozx-primary/10" />
          <div className="relative">
            <h2 className="font-display text-4xl sm:text-6xl font-medium tracking-tight mb-6 max-w-3xl mx-auto">
              Não fique de fora. <span className="text-ozx-primary">Florianópolis te espera.</span>
            </h2>
            <Button
              onClick={() => navigate("/checkout")}
              className="bg-ozx-primary hover:bg-ozx-primaryHover text-ozx-bg font-semibold rounded-full px-10 py-6 text-base glow-primary"
              data-testid="footer-cta"
            >
              Garantir minha vaga <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </motion.div>
      </section>

      <Footer event={event} />
    </div>
  );
}
